/* eslint-disable no-underscore-dangle */

import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { DataFieldGroup } from '../../shared/datafields/models';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { IntegratedFtpProviderEntity } from '../models/integrated-ftp-provider';
import { SystemIntegratedFtpProviderEntity } from '../models/system-integrated-ftp-provider';
import { DeveloperService } from '../services/developer.service';
import { conditionalValidator } from 'src/app/shared/validators/conditional-validator';
import { DataFieldGroupContainerComponent } from '../../shared/datafields/datafield-group-container.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';


@Component({
    selector: 'app-developer-system-integrated-ftp-provider',
    templateUrl: './developer-system-integrated-ftp-provider.component.html',
    styleUrls: ['./developer-system-integrated-ftp-provider.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, FormsModule, ReactiveFormsModule, DataFieldGroupContainerComponent]
})
export class DeveloperSystemIntegratedFtpProviderComponent implements OnInit {
  @ViewChild('dfgContainer', { static: false }) private dfgContainer: DataFieldGroupContainerComponent = null;

  hasCreatePerm = false;
  hasEditPerm = false;
  hasDeletePerm = false;

  systemIntegratedFtpProviderDisplayGroups: DataFieldGroup[] = [];

  integratedFtpProviders: IntegratedFtpProviderEntity[] = [];
  systemIntegratedFtpProvider: SystemIntegratedFtpProviderEntity = null;

  form: UntypedFormGroup;

  private editingInternal = true;

  constructor(private developerService: DeveloperService,
    private toastService: ToastService,
    private loadingPanelService: LoadingPanelService,
    private formBuilder: UntypedFormBuilder) {
  }

  get editing(): boolean {
    return this.editingInternal;
  }
  set editing(value: boolean) {
    this.editingInternal = value;
    if (value) {
      this.form?.enable({ emitEvent: false });
      this.dfgContainer?.edit();
    }
    else {
      this.form?.disable({ emitEvent: false });
      this.dfgContainer?.commit();
    }
  }

  ngOnInit(): void {
    this.newSystemIntegratedFtpProvider();
    this.form = this.formBuilder.group({
      providerSettings: this.formBuilder.group({
        provider: null,
        host: ['', conditionalValidator(() => this.fcProvider?.value !== null, Validators.required)]
      }),
      config: this.formBuilder.group({
      }),
    });

    this.editing = false;
    this.fcProvider.valueChanges.subscribe(value => {
      this.fcHost.updateValueAndValidity();
      this.setSystemIntegratedFtpProvider(value);
    });

    this.refresh();
  }

  async refresh(): Promise<void> {
    [this.integratedFtpProviders, this.systemIntegratedFtpProvider] = await Promise.all([
      this.developerService.getIntegratedFtpProviders(),
      this.developerService.getSystemIntegratedFtpProvider()
    ]);

    if (this.systemIntegratedFtpProvider) {
      await this.setSystemIntegratedFtpProvider(this.systemIntegratedFtpProvider.IntegratedFtpProviderId);
    } else {
      this.newSystemIntegratedFtpProvider();
    }
    this.fcHost.setValue(this.systemIntegratedFtpProvider.Host);
  }

  async setSystemIntegratedFtpProvider(integratedFtpProviderId: number): Promise<void> {
    this.systemIntegratedFtpProvider.IntegratedFtpProviderId = integratedFtpProviderId;
    if (integratedFtpProviderId) {
      this.fcProvider.setValue(this.systemIntegratedFtpProvider.IntegratedFtpProviderId, { emitEvent: false });

      // TODO: Skift til at kalde korrekt backend...
      this.systemIntegratedFtpProviderDisplayGroups = await this.developerService.getSystemIntegratedFtpProviderDataFieldGroups(integratedFtpProviderId, this.systemIntegratedFtpProvider?.Id);

      // Ensure that the dynamic fields reflect the current edit mode
      setTimeout(() => this.editing = this.editing, 100);
    }
    else {
      this.systemIntegratedFtpProviderDisplayGroups = [];
    }
  }

  newSystemIntegratedFtpProvider(): void {
    this.systemIntegratedFtpProvider = { Id: null, IntegratedFtpProviderId: null, Host: '', Configuration: '', CreatedBy: -1, CreatedDate: new Date(), UpdatedBy: null, UpdatedDate: null };
  }

  async save(): Promise<void> {
    if (!this.systemIntegratedFtpProvider.IntegratedFtpProviderId) {
      if (this.systemIntegratedFtpProvider.Id) {
        this.loadingPanelService.showLoadingPanelDirect('Deleting...');
        this.developerService.deleteSystemIntegratedFtpProvider(this.systemIntegratedFtpProvider.Id);
        this.loadingPanelService.hideLoadingPanel();
        this.newSystemIntegratedFtpProvider();
        this.toastService.showToast('System integrated ftp provider deleted...', ToastType.info);
      }
    }
    else {
      this.loadingPanelService.showLoadingPanelDirect('Saving...');

      this.systemIntegratedFtpProvider.IntegratedFtpProviderId = this.fcProvider.value;
      this.systemIntegratedFtpProvider.Host = this.fcHost.value;
      const datafieldValues = DataFieldGroup.getDataFieldValues(this.systemIntegratedFtpProviderDisplayGroups, true);
      this.systemIntegratedFtpProvider = await this.developerService.saveSystemIntegratedFtpProvider(this.systemIntegratedFtpProvider, datafieldValues);

      this.loadingPanelService.hideLoadingPanel();
      this.toastService.showToast('System integrated ftp provider saved...', ToastType.success);
    }

    this.editing = false;
  }

  edit(): void {
    this.editing = true;
  }

  cancel(): void {
    this.editing = false;
    this.systemIntegratedFtpProviderDisplayGroups = [];
    this.form.reset();
    this.refresh();
  }

  get fcHost(): UntypedFormControl {
    return this.form?.get('providerSettings.host') as UntypedFormControl;
  }

  get fcProvider(): UntypedFormControl {
    return this.form?.get('providerSettings.provider') as UntypedFormControl;
  }
}
